import { FC, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, XCircle } from 'lucide-react'
import { Motion } from '@/components/ui/motion'
import Header from '@/components/Header'
import BottomNavigation from '@/components/BottomNavigation'
import InstallerProjectsList from '@/components/InstallerProjectsList'
import { useProjectsInstaller, InstallerProject } from '@/hooks/useProjectsInstaller'
import { useTranslation } from '@/hooks/useTranslation'

const MyProjectsPage: FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [search, setSearch] = useState('')

  const { currentProjects, loading, error, refresh } = useProjectsInstaller()

  const filteredProjects = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return currentProjects

    return currentProjects.filter(item => {
      const p = item.project
      if (!p) return false
      return [p.projectNumber, p.businessName, p.customerFirstName, p.customerLastName, p.address, p.city]
        .filter(Boolean)
        .some(field => String(field).toLowerCase().includes(query))
    })
  }, [currentProjects, search])

  const handleSelect = (project: InstallerProject) => {
    if (project.project?.id) navigate(`/project/${project.project.id}`)
  }

  return (
    <div className='min-h-screen bg-[hsl(var(--background))] pb-24'>
      <Header title={t('MY_PROJECTS_TITLE')} />
      <div className='container mx-auto px-4 py-6'>
        <Motion variant='fadeIn' duration={500}>
          <p className='text-sm text-[hsl(var(--muted-foreground))] mb-4'>{t('MY_PROJECTS_SUBTITLE')}</p>

          {loading ? (
            <div className='flex flex-col items-center justify-center py-12 gap-4'>
              <span className='spinner spinner-lg' style={{ borderTopColor: 'var(--brand-red)' }} />
              <p className='text-sm'>{t('INSTALLER_LOADING')}</p>
            </div>
          ) : error ? (
            <div className='rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]'>
              <div className='flex items-center justify-center p-10'>
                <div className='text-center'>
                  <XCircle className='mx-auto h-10 w-10 mb-3 text-red-500' />
                  <h4 className='text-lg mb-2'>{t('INSTALLER_ERROR_TITLE')}</h4>
                  <p className='text-sm text-[hsl(var(--muted-foreground))] mb-4'>{error}</p>
                  <button
                    className='h-10 px-4 rounded-[var(--radius)] bg-[hsl(var(--primary))] text-white text-sm font-medium hover:bg-[hsl(var(--primary))]/90 transition-colors'
                    onClick={refresh}
                  >
                    {t('INSTALLER_TRY_AGAIN')}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {currentProjects.length > 4 && (
                <div className='relative mb-4'>
                  <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--muted-foreground))]' />
                  <input
                    type='text'
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder={t('MY_PROJECTS_SEARCH_PLACEHOLDER')}
                    className='w-full h-10 pl-9 pr-3 rounded-[var(--radius)] border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-sm outline-none focus:border-[var(--brand-blue)]'
                  />
                </div>
              )}

              <InstallerProjectsList projects={filteredProjects} onSelect={handleSelect} />
            </>
          )}
        </Motion>
      </div>
      <BottomNavigation />
    </div>
  )
}

export default MyProjectsPage
